import { useEffect, useRef, useCallback } from 'react';
import type { UploadedMedia } from './MediaUpload';

/**
 * Available image size variants.
 */
export type ImageSize = 'small' | 'medium' | 'large';

/**
 * Props for the ImageSizeSelector component.
 */
interface ImageSizeSelectorProps {
    /** The media item being inserted */
    media: UploadedMedia;
    /** Position to display the selector (screen coordinates) */
    position: { x: number; y: number };
    /** Callback when a size is selected */
    onSelect: (size: ImageSize, media: UploadedMedia) => void;
    /** Callback when selection is cancelled */
    onCancel: () => void;
}

/**
 * Size option configuration for display.
 */
interface SizeOption {
    key: ImageSize;
    label: string;
    description: string;
    shortcut: string;
}

/** Available size options with labels and keyboard shortcuts */
const SIZE_OPTIONS: SizeOption[] = [
    { key: 'small', label: 'Small', description: '150px - Thumbnail', shortcut: 'S' },
    { key: 'medium', label: 'Medium', description: '600px - Inline', shortcut: 'M' },
    { key: 'large', label: 'Large', description: '1200px - Full width', shortcut: 'L' },
];

/**
 * ImageSizeSelector displays a floating menu for selecting image size variant.
 * Appears after dropping an image into the editor.
 * Supports keyboard shortcuts (S, M, L) for quick selection.
 */
export function ImageSizeSelector({
    media,
    position,
    onSelect,
    onCancel,
}: ImageSizeSelectorProps) {
    const menuRef = useRef<HTMLDivElement>(null);

    /**
     * Handle keyboard shortcuts for size selection.
     */
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const key = e.key.toUpperCase();
        
        // Check for size shortcuts
        const option = SIZE_OPTIONS.find(opt => opt.shortcut === key);
        if (option) {
            e.preventDefault();
            onSelect(option.key, media);
            return;
        }

        // Escape to cancel
        if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
        }
    }, [media, onSelect, onCancel]);

    /**
     * Handle clicks outside the menu to cancel.
     */
    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
            onCancel();
        }
    }, [onCancel]);

    // Set up event listeners
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleKeyDown, handleClickOutside]);

    // Focus the menu for keyboard accessibility
    useEffect(() => {
        menuRef.current?.focus();
    }, []);

    // Calculate position to keep menu on screen
    const menuStyle: React.CSSProperties = {
        position: 'fixed',
        left: Math.min(position.x, window.innerWidth - 220),
        top: Math.min(position.y, window.innerHeight - 200),
        zIndex: 9999,
    };

    return (
        <div
            ref={menuRef}
            style={menuStyle}
            className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-2 min-w-[200px]"
            tabIndex={-1}
        >
            {/* Header with image preview */}
            <div className="flex items-center gap-2 px-2 py-1 mb-2 border-b border-slate-700">
                <img
                    src={media.urls.small}
                    alt={media.alt_text || media.original_filename}
                    className="w-8 h-8 object-cover rounded"
                />
                <span className="text-xs text-gray-400 truncate flex-1">
                    {media.original_filename}
                </span>
            </div>

            {/* Size options */}
            <div className="text-xs text-gray-500 px-2 mb-1">Select size:</div>
            {SIZE_OPTIONS.map((option) => (
                <button
                    key={option.key}
                    onClick={() => onSelect(option.key, media)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-gray-200 hover:bg-slate-700 rounded transition-colors"
                >
                    <div>
                        <span className="font-medium">{option.label}</span>
                        <span className="text-gray-500 ml-2">{option.description}</span>
                    </div>
                    <kbd className="px-1.5 py-0.5 text-xs bg-slate-700 text-gray-400 rounded">
                        {option.shortcut}
                    </kbd>
                </button>
            ))}

            {/* Cancel hint */}
            <div className="text-xs text-gray-500 px-2 pt-2 mt-2 border-t border-slate-700">
                Press <kbd className="px-1 bg-slate-700 rounded">Esc</kbd> to cancel
            </div>
        </div>
    );
}

export default ImageSizeSelector;

